import React, { useCallback, useEffect, useRef, useState } from "react";
import { Flame, Headphones, Link2, Loader2, Mic, MicOff, Timer, Users, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PRAYER_ACCENT, excerpt, fill, prayerExcerptChars } from "@/lib/prayers";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { readVoicePref, uploadPrayerVoice, usePrayerVoice, voiceSupported, writeVoicePref } from "./usePrayerVoice";

export const URGENT_COLOR = "#D97757";
const MINUTE = 60;
const MIN_PARTIAL = 30; // lot 660 : « Quitter » → la prière écourtée (et sa voix) ne compte qu'à partir de la moitié
const POLL_MS = 10000;

// Live counters, refreshed every 10 s while `active` (dialog open / banner visible).
export const useChainLive = (prayerId, active, initial) => {
  const [state, setState] = useState(initial || null);
  const load = useCallback(() => api.get(`/prayers/${prayerId}/chain`).then(({ data }) => setState(data)).catch(() => {}), [prayerId]);
  useEffect(() => {
    if (!active) return undefined;
    load();
    const id = setInterval(() => { if (document.visibilityState === "visible") load(); }, POLL_MS);
    return () => clearInterval(id);
  }, [active, load]);
  return [state, setState, load];
};

// « 12 min · 8 personnes · 2 en prière en ce moment »
export const ChainCounters = ({ chain, compact = false, testId, center = false }) => {
  const { t } = useApp();
  const c = chain || {};
  const cls = compact ? "text-[11px]" : "text-xs";
  return (
    <div className={`flex items-center gap-x-3 gap-y-1 flex-wrap ${center ? "justify-center" : ""} ${cls}`} style={{ color: "var(--yalola-text-muted)" }} data-testid={testId}>
      <span className="inline-flex items-center gap-1 font-semibold" style={{ color: PRAYER_ACCENT }}><Timer size={12} /> {fill(t("prayers.chain_minutes", "{n} min de prière"), { n: c.minutes || 0 })}</span>
      <span className="inline-flex items-center gap-1"><Users size={12} /> {fill(t("prayers.chain_people", "{n} personne(s)"), { n: c.people || 0 })}</span>
      {c.praying_now > 0 && (
        <span className="inline-flex items-center gap-1 font-semibold" style={{ color: URGENT_COLOR }} data-testid={testId ? `${testId}-now` : undefined}>
          <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: URGENT_COLOR }} /><span className="relative inline-flex rounded-full h-2 w-2" style={{ background: URGENT_COLOR }} /></span>
          {fill(t("prayers.chain_now", "{n} en prière en ce moment"), { n: c.praying_now })}
        </span>
      )}
      {!c.minutes && !c.praying_now && <span>{t("prayers.chain_none_yet", "Sois le premier maillon de la chaîne")}</span>}
    </div>
  );
};

const Ring = ({ progress, label }) => {
  const r = 84, c = 2 * Math.PI * r;
  return (
    <div className="relative mx-auto" style={{ width: 200, height: 200 }}>
      <svg width="200" height="200" viewBox="0 0 200 200" className="-rotate-90">
        <circle cx="100" cy="100" r={r} stroke="rgba(255,255,255,0.12)" strokeWidth="10" fill="none" />
        <circle cx="100" cy="100" r={r} stroke="#E0A63C" strokeWidth="10" fill="none" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - progress)} style={{ transition: "stroke-dashoffset 1s linear" }} />
      </svg>
      <div className="absolute inset-0 grid place-items-center text-5xl font-bold tabular-nums" style={{ fontFamily: "Outfit", color: "#FAF7F2" }} data-testid="prayer-minute-countdown">{label}</div>
    </div>
  );
};

// Full « Prie 1 minute maintenant » experience: 60 s ring → POST complete → thanks + live counters.
export const PrayerMinuteDialog = ({ p, open, onOpenChange, onCompleted }) => {
  const { t, user, config } = useApp();
  const [phase, setPhase] = useState("idle"); // idle | running | sending | done
  const [left, setLeft] = useState(MINUTE);
  const [session, setSession] = useState(null);
  const [partial, setPartial] = useState(false); // lot 656 : « done » après un « Quitter » compté
  const [voice, setVoice] = useState(readVoicePref); // lot 657 : « Prier à voix haute »
  const [voiceSent, setVoiceSent] = useState(false);
  const rec = usePrayerVoice();
  const [live, setLive, reload] = useChainLive(p?.id, open, p?.chain);
  const [full, setFull] = useState(false); // lot 674 : « Lire la suite » sur le texte de la demande
  const timerRef = useRef(null);
  const busyRef = useRef(false);
  const voiceOk = voiceSupported();
  const firstName = (p?.author?.name || "").split(" ")[0];

  const stop = () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };
  const toggleVoice = () => {
    if (!voiceOk) return toast.error(t("prayers.voice_unsupported", "Ton appareil ne permet pas l'enregistrement vocal ici."));
    setVoice((v) => { writeVoicePref(!v); return !v; });
  };

  const start = async () => {
    if (!user) return toast.error(t("prayers.chain_login", "Connecte-toi pour rejoindre la chaîne"));
    try {
      const { data } = await api.post(`/prayers/${p.id}/chain/start`);
      setSession(data.session_id);
      setLive((s) => ({ ...(s || {}), minutes: data.minutes, people: data.people, praying_now: data.praying_now, mine: data.mine }));
      setLeft(MINUTE);
      setVoiceSent(false);
      setPhase("running");
      if (voice && voiceOk && !(await rec.start())) toast.error(t("prayers.voice_mic_denied", "Micro refusé — ta prière reste silencieuse."));
      const startedAt = Date.now();
      stop();
      timerRef.current = setInterval(() => {
        const remaining = Math.max(0, MINUTE - Math.floor((Date.now() - startedAt) / 1000));
        setLeft(remaining);
        if (remaining <= 0) { stop(); complete(data.session_id); }
      }, 250);
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
  };

  // Lot 657 : fin de l'enregistrement → envoi du fichier → champs audio_* du POST complete / leave.
  // Lot 682 : si le serveur mesure un fichier MUET (micro non capté), on n'envoie pas un vocal silencieux au demandeur
  // et on le dit à celui qui prie — sa prière compte quand même.
  const finishVoice = async () => {
    const res = await rec.stop();
    if (!res) return {};
    setPhase("sending");
    try {
      const up = await uploadPrayerVoice(res.blob, config);
      if (!up) return {};
      if (up.silent) {
        toast.warning(t("prayers.voice_silent", "Ta voix n'a pas été captée (micro muet) — ta prière compte, sans le vocal."));
        return {};
      }
      return { audio_url: up.url, audio_duration: res.duration || up.duration };
    } catch {
      toast.error(t("prayers.voice_upload_failed", "Ta prière a compté, mais la voix n'a pas pu être envoyée."));
      return {};
    }
  };
  const afterCounted = (data) => {
    setLive((s) => ({ ...(s || {}), minutes: data.minutes, people: data.people, praying_now: data.praying_now, mine: true }));
    setVoiceSent(!!data.has_audio);
    setPhase("done");
    onCompleted?.(p.id, data);
    window.dispatchEvent(new Event("yalola:prayer_changed"));
  };

  const complete = async (sid) => {
    if (busyRef.current) return;
    busyRef.current = true;
    try {
      const extra = await finishVoice();
      const { data } = await api.post(`/prayers/${p.id}/chain/complete`, { session_id: sid, ...extra });
      afterCounted(data);
    } catch (e) {
      toast.error(e?.friendlyMessage || t("common.error", "Erreur"));
      setPhase("idle");
    } finally { busyRef.current = false; }
  };

  // Lot 656/660 : « Quitter » ferme la session côté serveur ; à partir de 30 s la prière écourtée compte quand même.
  const leave = async (sid, { close = false } = {}) => {
    stop();
    if (!sid || busyRef.current) { if (close) onOpenChange(false); return; }
    busyRef.current = true;
    try {
      // trop court pour compter → la voix est jetée sans l'envoyer
      let extra = {};
      if (MINUTE - left >= MIN_PARTIAL) extra = await finishVoice(); else rec.discard();
      const { data } = await api.post(`/prayers/${p.id}/chain/leave`, { session_id: sid, ...extra });
      if (data.counted && !close) {
        setPartial(true);
        afterCounted(data);
      } else {
        if (data.counted) { toast.success(t("prayers.chain_partial_done", "Merci 🙏 Même écourtée, ta prière compte.")); onCompleted?.(p.id, data); window.dispatchEvent(new Event("yalola:prayer_changed")); }
        setPhase("idle");
        onOpenChange(false); // trop court ou fermeture extérieure → la boîte se ferme
      }
    } catch {
      setPhase("idle");
      if (close) onOpenChange(false);
    } finally { busyRef.current = false; }
  };

  useEffect(() => {
    if (!open) { stop(); rec.discard(); setPhase("idle"); setLeft(MINUTE); setSession(null); setPartial(false); setVoiceSent(false); }
    return stop;
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (phase === "done") reload(); }, [phase, reload]);

  if (!p) return null;
  const mm = String(Math.floor(left / 60)).padStart(1, "0");
  const ss = String(left % 60).padStart(2, "0");
  const closeNow = () => { if (phase === "running") return leave(session, { close: true }); stop(); onOpenChange(false); };
  const limit = prayerExcerptChars(config);
  const long = (p.content || "").trim().length > limit;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && phase === "running") return leave(session, { close: true }); if (!o) stop(); onOpenChange(o); }}>
      {/* Lot 674 : fond sombre → la croix par défaut (texte sombre) était invisible : forcée en clair + bouton « Fermer » en bas. */}
      <DialogContent className="sm:max-w-md p-0 overflow-hidden border-0 [&_[data-dialog-close]]:text-[#FAF7F2] [&_[data-dialog-close]]:opacity-80 [&_[data-dialog-close]]:h-9 [&_[data-dialog-close]]:w-9 [&_[data-dialog-close]]:grid [&_[data-dialog-close]]:place-items-center [&_[data-dialog-close]]:rounded-full [&_[data-dialog-close]]:bg-white/10" style={{ background: "#0E1A14" }} data-testid={`prayer-minute-dialog-${p.id}`}>
        <div className="p-6 space-y-5 text-center" style={{ color: "#FAF7F2" }}>
          <DialogTitle className="text-lg font-bold flex items-center justify-center gap-2" style={{ fontFamily: "Outfit" }}>
            <Link2 size={18} color="#E0A63C" /> {t("prayers.chain_title", "Chaîne de prière")}
          </DialogTitle>
          <DialogDescription className="text-sm" style={{ color: "#9AA1AA" }}>
            {p.author?.name ? fill(t("prayers.chain_needs", "{name} a besoin de prière maintenant"), { name: p.author.name }) : ""}
          </DialogDescription>
          <div>
            <blockquote className={`text-[15px] leading-relaxed whitespace-pre-wrap break-words rounded-2xl p-4 text-left ${full ? "max-h-[34vh] overflow-y-auto" : ""}`} style={{ background: "#16261D" }} data-testid="prayer-minute-text" data-full={full ? "1" : "0"}>
              {full || !long ? p.content : excerpt(p.content, limit)}
            </blockquote>
            {long && (
              <button type="button" onClick={() => setFull((v) => !v)} className="mt-2 text-sm font-semibold hover:underline" style={{ color: "#E0A63C", background: "transparent" }} data-testid="prayer-minute-text-toggle">
                {full ? t("nav.see_less", "Voir moins") : t("prayers.read_more", "Lire la suite")}
              </button>
            )}
          </div>

          {phase !== "done" ? (
            <>
              <Ring progress={phase === "running" ? left / MINUTE : phase === "sending" ? 0 : 1} label={phase === "running" ? `${mm}:${ss}` : phase === "sending" ? "…" : "1:00"} />
              {phase === "sending" ? (
                <p className="text-xs inline-flex items-center gap-1.5 justify-center" style={{ color: "#E0A63C" }} data-testid="prayer-minute-sending"><Loader2 size={13} className="animate-spin" /> {t("prayers.voice_uploading", "Envoi de ta prière…")}</p>
              ) : rec.recording ? (
                <p className="text-xs inline-flex items-center gap-1.5 justify-center font-semibold" style={{ color: URGENT_COLOR }} data-testid="prayer-minute-recording">
                  <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: URGENT_COLOR }} /><span className="relative inline-flex rounded-full h-2 w-2" style={{ background: URGENT_COLOR }} /></span>
                  {t("prayers.voice_recording", "Enregistrement en cours…")}
                </p>
              ) : (
                <p className="text-xs" style={{ color: "#9AA1AA" }}>{t("prayers.chain_timer_hint", "Ferme les yeux, respire, et porte cette demande devant Dieu.")}</p>
              )}
              {phase === "idle" ? (
                <>
                  {/* Lot 657 : « Prier à voix haute » — la minute est enregistrée et envoyée à l'auteur seul */}
                  <button type="button" onClick={toggleVoice} aria-pressed={voice && voiceOk} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors"
                    style={voice && voiceOk ? { background: URGENT_COLOR, borderColor: URGENT_COLOR, color: "#fff" } : { background: "transparent", borderColor: "rgba(255,255,255,0.25)", color: "#C9D1CB" }}
                    data-testid="prayer-minute-voice-toggle" data-on={voice && voiceOk ? "1" : "0"}>
                    {voice && voiceOk ? <Mic size={13} /> : <MicOff size={13} />} {t("prayers.voice_toggle", "Prier à voix haute")}
                  </button>
                  {voice && voiceOk && <p className="text-[11px]" style={{ color: "#9AA1AA" }} data-testid="prayer-minute-voice-hint">{fill(t("prayers.voice_hint", "Ta voix est enregistrée pendant la minute et envoyée à {name} seulement."), { name: firstName || p.author?.name || "" })}</p>}
                  <Button className="rounded-full w-full h-12 text-base font-semibold" style={{ background: "#E0A63C", color: "#0E1A14" }} onClick={start} data-testid="prayer-minute-start">
                    <Flame size={18} className="mr-1" /> {live?.mine ? t("prayers.chain_pray_again", "Prier encore 1 minute") : t("prayers.chain_pray_now", "Prie 1 minute maintenant")}
                  </Button>
                </>
              ) : phase === "running" ? (
                <button type="button" className="text-sm font-semibold underline-offset-2 hover:underline" style={{ color: "#9AA1AA", background: "transparent" }} onClick={() => leave(session)} data-testid="prayer-minute-leave">{t("prayers.chain_leave", "Quitter")}</button>
              ) : null}
            </>
          ) : (
            <div className="space-y-3" data-testid="prayer-minute-done" data-partial={partial ? "1" : "0"} data-voice={voiceSent ? "1" : "0"}>
              <div className="mx-auto h-20 w-20 rounded-full grid place-items-center text-4xl" style={{ background: "#E0A63C22" }}>🙏</div>
              <p className="font-semibold">{partial ? t("prayers.chain_partial_done", "Merci 🙏 Même écourtée, ta prière compte.") : t("prayers.chain_done", "Merci 🙏 Ta minute de prière compte.")}</p>
              {voiceSent && <p className="text-xs inline-flex items-center gap-1 justify-center" style={{ color: "#E0A63C" }} data-testid="prayer-minute-voice-sent"><Headphones size={13} /> {fill(t("prayers.voice_sent", "{name} pourra écouter ta prière 🎧"), { name: firstName || p.author?.name || "" })}</p>}
              <Button variant="outline" className="rounded-full" style={{ borderColor: "#E0A63C", color: "#E0A63C", background: "transparent" }} onClick={() => { setPartial(false); start(); }} data-testid="prayer-minute-again">
                {t("prayers.chain_pray_again", "Prier encore 1 minute")}
              </Button>
            </div>
          )}

          <div className="[&_*]:!text-[#C9D1CB]"><ChainCounters chain={live || p.chain} testId="prayer-minute-counters" center /></div>
          {phase !== "running" && (
            <button type="button" onClick={closeNow} className="inline-flex items-center gap-1.5 rounded-full px-5 py-2 text-sm font-semibold border" style={{ borderColor: "rgba(255,255,255,0.25)", color: "#C9D1CB", background: "transparent" }} data-testid="prayer-minute-close">
              <X size={14} /> {t("common.close", "Fermer")}
            </button>
          )}
          {session && <span className="sr-only" data-testid="prayer-minute-session">{session}</span>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const PrayMinuteButton = ({ p, onCompleted, size = "sm", className = "", autoOpen = false }) => {
  const { t, user, config } = useApp();
  const [open, setOpen] = useState(!!autoOpen && !!user);
  if (config?.settings?.features?.prayer_chain === false) return null;
  return (
    <>
      <Button size={size} className={`rounded-full font-semibold ${className}`} style={{ background: URGENT_COLOR, color: "#fff" }}
        onClick={(e) => { e.stopPropagation(); if (!user) return toast.error(t("prayers.chain_login", "Connecte-toi pour rejoindre la chaîne")); setOpen(true); }}
        data-testid={`prayer-minute-open-${p.id}`}>
        {p.chain?.mine ? <Loader2 size={14} className="mr-1 hidden" /> : null}
        <Flame size={14} className="mr-1" /> {p.chain?.mine ? t("prayers.chain_pray_again", "Prier encore 1 minute") : t("prayers.chain_pray_now", "Prie 1 minute maintenant")}
      </Button>
      <PrayerMinuteDialog p={p} open={open} onOpenChange={setOpen} onCompleted={onCompleted} />
    </>
  );
};
